import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RegistrationCodeForm, { type RegistrationResponse } from "../components/RegistrationCodeForm";
import { clearStoredClient, useClientContext } from "../context/ClientContext";
import { useAuth } from "../auth/AuthContext";
import { useDomain } from "../context/DomainContext";
import { useTheme } from "../theme/ThemeContext";
import { useDevMode } from "../context/DevModeContext";
import "./Styles/LandingPage.css";
import { Message } from "../components/Message";

const SKIP_PERSONNEL_ID = "123bc4ca-6e3a-4f59-b935-aba72a000223";

// Check if URL indicates we're returning from an auth redirect
function isReturningFromAuthRedirect(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  // MSAL redirect responses contain these params
  return hash.includes("code=") || 
         hash.includes("id_token=") || 
         hash.includes("error=") ||
         search.includes("code=") ||
         search.includes("error=");
}

export default function LandingPage() {
  const { isAuthenticated, isLoading, getEmail, getName, getUserId, login, logout } = useAuth();
  const { client, setClient } = useClientContext();
  const { matchedEnvironment, isLoading: domainLoading, hasError: domainError, domainMatchedBrand } = useDomain();
  const { applyThemePreference, signOutTheme } = useTheme();
  const { devMode } = useDevMode();
  const navigate = useNavigate();
  const loginAttempted = useRef(false);
  const mappingChecked = useRef(false);
  const [processingRedirect, setProcessingRedirect] = useState(() => isReturningFromAuthRedirect());
  const [checkingMapping, setCheckingMapping] = useState(false);

  const email = getEmail();
  const userId = getUserId();
  
  // Use the environment from domain config (matched by URL)
  const env = matchedEnvironment;

  // Store user context in sessionStorage for DomainContext to use when saving theme
  useEffect(() => {
    if (userId && env) {
      sessionStorage.setItem("currentUserId", userId);
      sessionStorage.setItem("currentEnvironment", env);
    }
  }, [userId, env]);

  // Clear the processing flag once MSAL is done loading
  useEffect(() => {
    if (!isLoading && processingRedirect) {
      setProcessingRedirect(false);
    }
  }, [isLoading, processingRedirect]);

  // Auto-login when not authenticated (but not if we're processing a redirect)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !loginAttempted.current && !processingRedirect) {
      // Small delay to ensure MSAL has fully processed any redirect response
      const timer = setTimeout(() => {
        if (!loginAttempted.current) {
          loginAttempted.current = true;
          login();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isAuthenticated, login, processingRedirect]);

  // Check for existing user-personnel mapping when authenticated
  useEffect(() => {
    async function checkExistingMapping() {
      // Wait for brand config to load (so we have the correct environment)
      // Runs when a client is already stored, so a refresh still applies the server preference.
      if (domainLoading || domainError || !isAuthenticated || !userId || mappingChecked.current) {
        return;
      }

      mappingChecked.current = true;
      // A stored client is already on the app. Still read the server preference, but do not
      // flash the checking screen or re-run registration validate.
      const alreadySignedIn = client != null;
      if (!alreadySignedIn) setCheckingMapping(true);

      try {
        // Use the Azure Function proxy (same origin, no CORS issues)
        const res = await fetch(`/api/usermapping/${encodeURIComponent(userId)}?env=${env}`);
        
        if (res.ok) {
          const data = await res.json();
          
          // Check both camelCase and PascalCase property names
          const found = data.found ?? data.Found;
          const personnelId = data.personnelId ?? data.PersonnelId;
          const themePreference = data.themePreference ?? data.ThemePreference;
          
          // Apply user's theme preference if they have one
          applyThemePreference(themePreference);

          if (alreadySignedIn) return;
          
          if (found && personnelId) {
            // User has an existing mapping - fetch their personnel data and log them in
            const validateRes = await fetch("/api/registration/validate", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: email || "",
                registrationCode: personnelId,
                env
              })
            });

            if (validateRes.ok) {
              const validateData = await validateRes.json();
              if (validateData.allowed) {
                setClient({
                  email: validateData.email || email || "",
                  personMetadata: {
                    ...validateData.personMetadata,
                    email: typeof validateData.personMetadata?.email === "string" && validateData.personMetadata.email !== ""
                      ? validateData.personMetadata.email
                      : (validateData.email || email || ""),
                  }
                });
                navigate("/app", { replace: true });
                return;
              }
            }
          }
        }
        // If no mapping found or fetch failed, user will see the registration form
      } catch (err) {
        console.error("Error checking user mapping:", err);
        // Non-fatal - user will just see the registration form
      } finally {
        setCheckingMapping(false);
      }
    }

    checkExistingMapping();
  }, [domainLoading, domainError, isAuthenticated, userId, email, env, client, setClient, navigate, applyThemePreference]);

  // Redirect to /app when client is already set. An empty brand list has already
  // been sent to /error; following the stored client would leave that page.
  useEffect(() => {
    if (domainError || !client) return;
    navigate("/app", { replace: true });
  }, [client, domainError, navigate]);

  // Handle logout - clear client data before MSAL logout
  const handleLogout = useCallback(() => {
    signOutTheme();
    clearStoredClient();
    logout();
  }, [logout, signOutTheme]);

  // Show loading state while checking auth or brand config
  if (isLoading || domainLoading) {
    return (
      <div className="landing-root">
        <div className="landing-card">
          <p style={{ color: "var(--nav-fg)" }}>Checking authentication…</p>
        </div>
      </div>
    );
  }

  // If brand config failed to load (no brands), redirect to error
  if (domainError) {
    return (
      <div className="landing-root">
        <div className="landing-card">
          <h1 style={{ color: "var(--nav-fg)" }}>Configuration Error</h1>
          <Message type="error" text="No brand configuration available. Please contact support." />
        </div>
      </div>
    );
  }

  // If not authenticated, show redirecting message (login will be triggered by useEffect)
  if (!isAuthenticated) {
    return (
      <div className="landing-root">
        <div className="landing-card">
          <p style={{ color: "var(--nav-fg)" }}>Redirecting to login…</p>
        </div>
      </div>
    );
  }

  // Show loading state while checking for existing user mapping
  if (checkingMapping) {
    return (
      <div className="landing-root">
        <div className="landing-card">
          <p style={{ color: "var(--nav-fg)" }}>Checking your account…</p>
        </div>
      </div>
    );
  }

  // User is authenticated but hasn't completed PersonnelID validation yet
  if (!client) {
    const handleSkip = () => {
      // Minimal client payload for skip flow
      setClient({
        email: email || "",
        personMetadata: {
          email: email || "",
          name: getName() || "",
          personnelId: SKIP_PERSONNEL_ID
        }
      });

      navigate("/app", { replace: true });
    };

    const handleSuccess = (data: RegistrationResponse) => {
      setClient({
        email: data.email,
        personMetadata: {
          ...data.personMetadata,
          email: typeof data.personMetadata.email === "string" && data.personMetadata.email !== ""
            ? data.personMetadata.email
            : data.email,
        }
      });

      navigate("/app", { replace: true });
    };

    return (
      <div className="landing-root">
        <div className="landing-card">
          <h1 className="landing-card__title">Welcome to {domainMatchedBrand.displayName}</h1>
          <p className="landing-card__subtitle">Enter your personnel ID to continue</p>

          {devMode && (
            <div className="landing-card__dev-notice">
              <strong>Dev Mode:</strong> Use the skip button below if you don't have a Personnel ID handy.
            </div>
          )}

          <RegistrationCodeForm email={email || ""} userId={userId || undefined} env={env} onSuccess={handleSuccess} />

          <div className="landing-divider">or</div>

          <div className="landing-actions">
            <button
              type="button"
              className="landing-btn landing-btn--secondary"
              onClick={handleLogout}
            >
              Sign out
            </button>

            {devMode && (
              <button
                type="button"
                className="landing-btn landing-btn--danger"
                onClick={handleSkip}
              >
                Skip (dev only)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Client is set, redirect to app
  navigate("/app", { replace: true });
  return null;
}
